import type { Product } from "@/types";
import { compareNewest } from "@/lib/product-sort";
import { matchesCategories, productCategorySlugs } from "@/lib/category-filter"; // MOW-CATEGORIEFILTER-OKT26
import { splitFit } from "@/lib/filter-query"; // MOW-PASVORM-NOV26
import { productProperties } from "@/lib/product-properties"; // MOW-EIGENSCHAPPEN-NOV26

// Filter- en sorteerlogica van shop, categorie- en merkpagina's, zonder React of
// browser-API's: de server (app/shop/page.tsx, app/product-category/[...slug]/page.tsx)
// bepaalt hiermee welke producten in de eerste HTML horen, met exact dezelfde volgorde
// als de client daarna toont. Stond eerder in components/ShopSidebar.tsx ("use client"),
// en een server-component kan een functie uit een client-bestand niet aanroepen.
// ShopSidebar exporteert alles nog onder dezelfde naam.

export interface ShopFilters {
  search: string;
  /** Gekozen categorie-slugs (OF onder elkaar; een gekozen ouder omvat zijn kinderen), zie lib/category-filter.ts. */
  categories: string[];
  /**
   * Alleen producten in de sale (afgeprijsd of onder een lopende actie, lib/product-sale.ts). Staat hier maar
   * wordt NIET door applyFilters toegepast: daarvoor zijn de lopende acties nodig, die alleen de pagina kent.
   */
  sale: boolean;
  brands: string[];
  colors: string[];
  materials: string[];
  /** Model/pasvorm (productveld fit, MOW-PASVORM-NOV26). */
  fits: string[];
  /** Gekozen eigenschappen (sleutels uit lib/product-properties.ts, OF onder elkaar). MOW-EIGENSCHAPPEN-NOV26 */
  properties: string[];
  sizes: string[];
  priceMin: number;
  priceMax: number;
  sort: string;
}

export const DEFAULT_FILTERS: ShopFilters = {
  search: "", categories: [], sale: false, brands: [], colors: [], materials: [], fits: [], properties: [], sizes: [], priceMin: 0, priceMax: 200, sort: "relevance",
};

export function applyFilters(products: Product[], filters: ShopFilters): Product[] {
  // Eén "nu" voor de hele sortering, zodat de volgorde binnen één run niet kan verspringen.
  const now = Date.now();
  return products
    .filter((p) => {
      if (filters.search) {
        const words = filters.search.toLowerCase().split(/\s+/).filter(Boolean);
        const searchable = `${p.name} ${p.brand || ""} ${p.productType || ""} ${p.category || ""}`.toLowerCase();
        const matchesAll = words.every((w) => searchable.includes(w));
        if (!matchesAll) return false;
      }
      // MOW-CATEGORIEFILTER-OKT26: producten dragen alle voorouders van hun categorie, dus een gekozen ouder omvat zijn kinderen
      if (filters.categories?.length > 0 && !matchesCategories(productCategorySlugs(p), filters.categories)) return false;
      if (filters.brands.length > 0 && !filters.brands.includes(p.brand || "")) return false;
      if (filters.colors.length > 0) {
        const pColors = [...(p.mainColors || []), p.mainColor || ""].filter(Boolean);
        if (!filters.colors.some((c) => pColors.includes(c))) return false;
      }
      if (filters.materials.length > 0) {
        const pMats = (p.materials || []).map((m) => String(m).toLowerCase());
        if (!filters.materials.some((m) => pMats.includes(m.toLowerCase()))) return false;
      }
      // MOW-PASVORM-NOV26: een product kan twee pasvormen hebben ("Aansluitend, Normaal"); één treffer is genoeg
      if (filters.fits?.length > 0) {
        const pFits = splitFit(p.fit).map((f) => f.toLowerCase());
        if (!filters.fits.some((f) => pFits.includes(f.toLowerCase()))) return false;
      }
      // MOW-EIGENSCHAPPEN-NOV26: één treffer is genoeg
      if (filters.properties?.length > 0) {
        const pProps = productProperties(p);
        if (!filters.properties.some((k) => pProps.includes(k))) return false;
      }
      if (filters.sizes.length > 0) {
        const pSizes = (p.sizes || []).map((s) => String(s).toLowerCase());
        if (!filters.sizes.some((s) => pSizes.includes(s.toLowerCase()))) return false;
      }
      const price = p.salePrice ?? p.price ?? 0;
      if (price < filters.priceMin || price > filters.priceMax) return false;
      return true;
    })
    .map((p) => {
      if (filters.colors.length > 0 && p.colorOptions && p.colorOptions.length > 0) {
        const matched = p.colorOptions.filter((opt) => filters.colors.includes(opt.mainColor));
        if (matched.length > 0) return { ...p, colorOptions: matched };
      }
      return p;
    })
    .sort((a, b) => {
      const imgA = !!(a.images && a.images.length > 0 && a.images[0]);
      const imgB = !!(b.images && b.images.length > 0 && b.images[0]);
      if (imgA && !imgB) return -1;
      if (!imgA && imgB) return 1;
      switch (filters.sort) {
        case "price-asc": return (a.salePrice ?? a.price) - (b.salePrice ?? b.price);
        case "price-desc": return (b.salePrice ?? b.price) - (a.salePrice ?? a.price);
        case "name-asc": return a.name.localeCompare(b.name, "nl");
        // createdAt is in werkelijkheid een Firestore-Timestamp (of getal), geen Date:
        // `new Date(createdAt)` gaf NaN en de sortering deed niets (zie lib/product-sort.ts).
        case "newest":
          return compareNewest(a, b, now);
        default: {
          // "Bestverkocht": eerst op werkelijk aantal verkopen, dan op de
          // bestseller/aanbevolen-vlaggen, tenslotte nieuwste eerst.
          const sales = (Number(b.salesCount) || 0) - (Number(a.salesCount) || 0);
          if (sales !== 0) return sales;
          const rank = (p: Product) => (p.bestseller ? 2 : 0) + (p.featured ? 1 : 0);
          const r = rank(b) - rank(a);
          if (r !== 0) return r;
          return compareNewest(a, b, now);
        }
      }
    });
}
